import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { Alert } from '../../components/ui/index.js';

/**
 * Finance refusal codes, named.
 *
 * The finance API answers with a stable top-level `error.code` (already
 * covered by the generic `errors.*` catalogue — `VALIDATION_FAILED`,
 * `CONFLICT`, `INVALID_STATE_TRANSITION`, `DUPLICATE_BILLING`) PLUS a
 * `details[].code` naming exactly which business rule refused the request.
 * `ErrorNotice` alone would show "please check the highlighted fields" for
 * every one of these, which is honest but not the sentence CLAUDE.md section
 * 8 asks for — the interface shows the API's ACTUAL result. This module maps
 * every finance detail code (contract section 5, logs/f-api-summary.txt) to
 * its own sentence, in both languages, under `finance.errors.*`.
 */
const DETAIL_CODE_KEYS: Readonly<Record<string, string>> = {
  discount_exceeds_amount: 'discountExceedsAmount',
  price_override_reason_required: 'priceOverrideReasonRequired',
  service_not_of_client: 'serviceNotOfClient',
  due_date_before_issue_date: 'dueDateBeforeIssueDate',
  received_on_in_future: 'receivedOnInFuture',
  invoice_not_of_client: 'invoiceNotOfClient',
  period_reversed: 'periodReversed',
  amount_out_of_range: 'amountOutOfRange',
  currency_mismatch: 'currencyMismatch',
  draft_changed: 'draftChanged',
  tax_rate_changed: 'taxRateChanged',
  invoice_has_allocations: 'invoiceHasAllocations',
  adjustment_exceeds_balance: 'adjustmentExceedsBalance',
  allocation_exceeds_balance: 'allocationExceedsBalance',
  allocation_exceeds_payment: 'allocationExceedsPayment',
  invoice_not_draft: 'invoiceNotDraft',
  invoice_not_issued: 'invoiceNotIssued',
  invoice_has_no_lines: 'invoiceHasNoLines',
  service_cancelled: 'serviceCancelled',
  payment_not_recorded: 'paymentNotRecorded',
  payment_already_reversed: 'paymentAlreadyReversed',
  allocation_already_reversed: 'allocationAlreadyReversed',
  duplicate_billing: 'duplicateBilling',
  service_not_found: 'serviceNotFound',
  invoice_not_found: 'invoiceNotFound',
  'pricing.override': 'pricingOverrideRequired',
};

/** True when this error carries the named `details[].code`. */
export function hasDetailCode(error: unknown, code: string): boolean {
  return (
    error instanceof ApiError &&
    (error.details?.some((detail) => detail.code === code) ?? false)
  );
}

/** The first detail code this catalogue knows how to name, or null. */
function namedDetailCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  for (const detail of error.details) {
    if (detail.code && DETAIL_CODE_KEYS[detail.code]) return detail.code;
  }
  return null;
}

/**
 * The refusal, rendered as what actually happened.
 *
 * Shows the SPECIFIC sentence for a named finance detail code; falls back to
 * the generic `errors.<code>` message (via `ErrorNotice`) for anything else —
 * tenant isolation's `404`, a missing permission's `403`, a network failure.
 * Renders nothing for no error, exactly like `ErrorNotice`.
 */
export function FinanceMutationError({
  error,
}: {
  readonly error: unknown;
}) {
  const { t } = useTranslation();
  if (!error) return null;

  const code = namedDetailCode(error);
  if (code) {
    return (
      <Alert
        tone="warning"
        title={t(`finance.errors.${DETAIL_CODE_KEYS[code]}`)}
      />
    );
  }
  return <ErrorNotice error={error} />;
}
